import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/app/StatusBadge";
import PartnerCreditCard from "@/components/app/PartnerCreditCard";
import { paymentMethodLabel, sumOrdersTotal } from "@/lib/orders";
import { Download, Filter, RotateCcw } from "lucide-react";

const EMPTY_FILTERS = { status: "all", date_from: "", date_to: "" };

export default function PartnerOrders() {
  const [orders, setOrders] = useState([]);
  const [credit, setCredit] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const fetchOrders = useCallback(async (f = applied) => {
    const params = {};
    if (f.status && f.status !== "all") params.status = f.status;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  }, [applied]);

  useEffect(() => { fetchOrders(applied); }, [fetchOrders, applied]);
  useEffect(() => { api.get("/partners/me/credit").then(r => setCredit(r.data)).catch(() => {}); }, []);

  const applyFilters = () => setApplied({ ...filters });
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };

  const totalFiltered = sumOrdersTotal(orders);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">I miei ordini</h1>
          <p className="text-slate-500 mt-1">Storico ordini e stato pagamenti.</p>
        </div>
        <a href={`${API}/export/orders`} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" data-testid="orders-export"><Download className="w-4 h-4 mr-1" /> Esporta CSV</Button>
        </a>
      </div>

      {credit && <PartnerCreditCard credit={credit} compact />}

      <Card className="border-slate-200" data-testid="orders-filters-card">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs">Stato</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="inviato">Inviato</SelectItem>
                <SelectItem value="in_verifica">In verifica</SelectItem>
                <SelectItem value="confermato">Confermato</SelectItem>
                <SelectItem value="completato">Completato</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Data da</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="orders-date-from" />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Data a</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="orders-date-to" />
          </div>
          <Button onClick={applyFilters} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="orders-filter-apply">
            <Filter className="w-4 h-4 mr-1" /> Filtra
          </Button>
          <Button onClick={resetFilters} variant="outline" data-testid="orders-filter-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Azzera filtri
          </Button>
        </CardContent>
      </Card>

      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="orders-total-card">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{orders.length}</span> ordini visualizzati
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Totale Ordini Visualizzati</div>
            <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="orders-total-value">{eur(totalFiltered)}</div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ordine</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Voci</TableHead>
                <TableHead>Iscritti</TableHead>
                <TableHead>Totale</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Pagamento</TableHead>
                <TableHead>Modalità di Pagamento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.order_number}`}>
                  <TableCell><Link to={`/partner/ordini/${o.id}`} className="font-semibold text-[#0F2942] hover:underline">{o.order_number}</Link></TableCell>
                  <TableCell>{dateIt(o.created_at)}</TableCell>
                  <TableCell>{(o.items || []).length}</TableCell>
                  <TableCell>{o.enrollments_count}</TableCell>
                  <TableCell className="font-semibold">{eur(o.total)}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell><StatusBadge status={o.payment_status} /></TableCell>
                  <TableCell className="text-xs text-slate-700" data-testid={`order-payment-method-${o.order_number}`}>
                    {paymentMethodLabel(o.payment_method)}
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">Nessun ordine trovato.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
